import { useState } from "react";
import { motion } from "framer-motion";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Sparkles, Zap, Target, CheckCircle2, AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import CtaButton from "@/components/shared/CtaButton";
import PhoneOtpField from "@/components/shared/PhoneOtpField";
import AILogosLoader from "@/components/shared/AILogosLoader";
import { Reveal } from "@/components/motion/Reveal";
import { runVisibilityCheck, formatApiErrorDetail } from "@/lib/api";

const FIELD =
  "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#0B63F6] focus:ring-1 focus:ring-[#0B63F6] transition-colors";

const STATUS_COLOR = { strong: "#19A974", moderate: "#0B63F6", weak: "#F36F21" };

function scoreColor(s) {
  if (s >= 75) return "#19A974";
  if (s >= 50) return "#0B63F6";
  return "#F36F21";
}

function Gauge({ score }) {
  const data = [{ value: score, fill: scoreColor(score) }];
  return (
    <div className="relative h-52 w-52">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart innerRadius="78%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar background={{ fill: "#F0F1F3" }} dataKey="value" cornerRadius={20} angleAxisId={0} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-5xl font-black tracking-tightest" style={{ color: scoreColor(score) }}>{score}</span>
        <span className="text-xs text-gray-500 mt-1">/ 100 AI Visibility</span>
      </div>
    </div>
  );
}

export default function ToolsChecker() {
  const [form, setForm] = useState({ brand: "", website: "", industry: "", email: "", phone: "" });
  const [countryCode, setCountryCode] = useState("+91");
  const [otpToken, setOtpToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.website) return toast.error("Please enter your brand name and website.");
    if (!otpToken) return toast.error("Please verify your WhatsApp number with the OTP.");
    setLoading(true);
    setReport(null);
    try {
      const res = await runVisibilityCheck({
        ...form,
        country_code: countryCode,
        phone: form.phone,
        otp_token: otpToken,
      });
      setReport(res.report);
      toast.success("Your AI visibility report is ready.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Free AI Visibility Checker — GEO Score Tool"
        description="Instantly check how visible your brand is to AI search engines like ChatGPT, Gemini, Claude and Perplexity. Get a free GEO visibility score and recommendations from CompuBrain AI."
        path="/tools"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "AI Visibility Tool", path: "/tools" }])]}
      />
      <PageHero
        eyebrow="Free Tool"
        titleLines={["How visible is your", "brand to AI?"]}
        sub="Get an instant, AI-generated GEO visibility score across six categories — plus quick wins and strategic recommendations for your brand."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "AI Visibility Tool" }]}
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Input panel */}
          <div className="lg:col-span-4">
            <div className="rounded-2xl border border-gray-200 p-8 sticky top-24">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#0B63F6]"><Sparkles className="h-4 w-4" /> AI Visibility Checker</div>
              <form onSubmit={run} className="mt-6 space-y-4" data-testid="checker-form">
                <input className={FIELD} placeholder="Brand name*" value={form.brand} onChange={set("brand")} data-testid="checker-brand" />
                <input className={FIELD} placeholder="Website URL*" value={form.website} onChange={set("website")} data-testid="checker-website" />
                <input className={FIELD} placeholder="Industry (optional)" value={form.industry} onChange={set("industry")} data-testid="checker-industry" />
                <input className={FIELD} type="email" placeholder="Work email (for full report)" value={form.email} onChange={set("email")} data-testid="checker-email" />
                <PhoneOtpField
                  countryCode={countryCode}
                  phone={form.phone}
                  onCountryCode={setCountryCode}
                  onPhone={(v) => setForm((f) => ({ ...f, phone: v }))}
                  token={otpToken}
                  onToken={setOtpToken}
                />
                <button type="submit" disabled={loading} data-testid="checker-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#0B63F6] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#6E56CF] transition-colors disabled:opacity-70">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</> : <>Check my AI visibility <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p className="text-[11px] text-gray-400 text-center">Powered by CompuBrain AI&apos;s GEO analysis engine.</p>
              </form>
            </div>
          </div>

          {/* Report panel */}
          <div className="lg:col-span-8">
            {!report && !loading && (
              <div className="h-full rounded-2xl border border-dashed border-gray-300 flex flex-col items-center justify-center text-center p-16" data-testid="checker-empty">
                <div className="h-16 w-16 rounded-2xl bg-[#0B63F6]/[0.08] flex items-center justify-center text-[#0B63F6]"><Target className="h-8 w-8" /></div>
                <h3 className="font-display text-2xl font-bold tracking-tight mt-6">Your report will appear here</h3>
                <p className="text-gray-600 mt-2 max-w-md">Enter your brand and website to generate a real-time GEO visibility assessment across six ranking categories.</p>
              </div>
            )}

            {loading && (
              <div className="h-full rounded-2xl border border-gray-200 flex flex-col items-center justify-center text-center p-10" data-testid="checker-loading">
                <AILogosLoader />
              </div>
            )}

            {report && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-6" data-testid="checker-report">
                <div className="rounded-2xl border border-gray-200 p-8">
                  <div className="flex flex-col sm:flex-row items-center gap-8">
                    <Gauge score={report.overall_score} />
                    <div className="flex-1 text-center sm:text-left">
                      <div className="inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-4 py-1.5 text-sm font-bold">Grade {report.grade}</div>
                      <p className="text-lg text-[#111111] mt-4 leading-relaxed font-medium">{report.verdict}</p>
                      <p className="text-sm text-gray-500 mt-3">{report.citation_readiness}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 p-8">
                  <h3 className="font-display text-lg font-bold tracking-tight mb-6">Category breakdown</h3>
                  <div className="space-y-5">
                    {report.categories?.map((c) => (
                      <div key={c.name}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-[#111111]">{c.name}</span>
                          <span className="font-display font-bold" style={{ color: STATUS_COLOR[c.status] || "#0B63F6" }}>{c.score}</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${c.score}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className="h-full rounded-full" style={{ backgroundColor: STATUS_COLOR[c.status] || "#0B63F6" }} />
                        </div>
                        <p className="text-[13px] text-gray-500 mt-1.5">{c.insight}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-gray-200 p-8">
                    <h3 className="font-display text-lg font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-[#19A974]" /> Quick wins</h3>
                    <ul className="mt-4 space-y-3">
                      {report.quick_wins?.map((w, i) => (
                        <li key={i} className="flex items-start gap-3 text-[15px] text-gray-700 leading-relaxed"><CheckCircle2 className="h-4 w-4 text-[#19A974] mt-1 shrink-0" /> {w}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-gray-200 p-8">
                    <h3 className="font-display text-lg font-bold flex items-center gap-2"><Target className="h-5 w-5 text-[#6E56CF]" /> Strategic recommendations</h3>
                    <ul className="mt-4 space-y-3">
                      {report.strategic_recommendations?.map((w, i) => (
                        <li key={i} className="flex items-start gap-3 text-[15px] text-gray-700 leading-relaxed"><AlertCircle className="h-4 w-4 text-[#6E56CF] mt-1 shrink-0" /> {w}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-[#111111] text-white p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div>
                    <h3 className="font-display text-xl font-bold tracking-tight">Want the full, expert-verified audit?</h3>
                    <p className="text-[#A1A1AA] text-[15px] mt-2">Our GEO team will benchmark you against competitors and build your roadmap.</p>
                  </div>
                  <CtaButton variant="accent" source="ai-visibility-checker" service="AI Visibility Audit" title="Request Full AI Visibility Audit" testId="checker-full-audit-cta" className="shrink-0">Request full audit</CtaButton>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
